import React, { useContext } from 'react';
import { useNavigate, NavigateOptions } from 'react-router-dom';
import capitalize from 'lodash/capitalize';
import { v4 as uuidv4 } from 'uuid';

import { TranslateContext } from "../../../widgets/MyDataHelps/TranslateContext";
import { updateSearchParams } from '../../../api/utils';
import {
  isValidEmail,
  isValidEmailOrPhone,
  isValidVerificationCode,
  isValidNonce,
  getProjectCode,
  getLinkKey,
  getUtmParameters,
  getClickIdParameters,
  validateEmail,
  isKebabCase,
  isPascalCase,
} from '../utils'
import { AuthenticationContext, AuthenticationDispatchContext } from './AuthenticationContext';
import { AuthenticationConfigurationContext } from './Configuration';

import {
  getProjectByCode,
  getAccountStatus,
  finishEmailJoin,
  finishLogin as postFinishLogin,
  finishPasswordResetNonce,
  finishPasswordResetPhone,
  finishPhoneJoin,
  forgotPassword as postForgotPassword,
  getProjectByLinkKey,
  sendVerificationCode,
  sendVerificationCodeForPasswordReset,
  trackRegistrationEvent as postTrackRegistrationEvent,
  validateIdentifier as postValidateIdentifier,
  verifyEmail as postVerifyEmail,
  verifyEmailUpdate as postVerifyEmailUpdate,
  verifyPasswordResetNonce as postVerifyPasswordResetNonce,
  verifyPhone,
  validatePasswordRequirements as postValidatePasswordRequirements,
} from '../../../api/participant/authentication'
import { makeUrl } from '../../../api/makeUrl';

import {
  type ClientError,
  type Project,
  type AccountStatus,
  type Brand,
  type Locale,
} from '../types'
import { ProductNames } from './Authentication';

// For defining the type of the authentication state
type ValueOf<T> = T[keyof T];
export enum AuthenticationViews {
  'splash' = 'splash',
  'identifierCheck' = 'identifier-check',
  'join' = 'join',
  'verifyPhone' = 'verify-phone',
  'setPassword' = 'set-password',
  'login' = 'login',
  'loginPassword' = 'login-password',
  'knownUserLogin' = 'known-user-login',
  'verifyNonce' = 'verify-nonce',
  'verifyEmail' = 'verify-email',
  'forgotPassword' = 'forgot-password',
  'resetPassword' = 'reset-password',
  'verifyEmailUpdate' = 'verify-email-update',
  'externalLogin' = 'external-login',
  'registrationCheck' = 'registration-check',
}

export type State = {
  accountStatus: AccountStatus | undefined;
  activeView?: AuthenticationViews;
  brand: Brand | undefined;
  clientErrors: ClientError[];
  currentLanguage: string;
  emailOrPhone: string;
  identifier: string;
  identifierNonce: string | undefined;
  isLoading: boolean;
  lastSessionCookie: string;
  loginCheckTimer: any;
  project: Project | undefined;
  redirectUrl: string;
  rememberMe: boolean;
  resendCodeReady: boolean;
  resendWait: number;
  serverError: string;
  verificationCode: string;
  verifiedNonceToken: string;
  verifiedPhoneToken: string;
};

type Action =
  | { type: 'SET_ACCOUNT_STATUS'; payload: AccountStatus | undefined }
  | { type: 'SET_ACTIVE_VIEW'; payload: AuthenticationViews }
  | { type: 'SET_CLIENT_ERRORS'; payload: ClientError[] }
  | { type: 'SET_CURRENT_LANGUAGE'; payload: string }
  | { type: 'SET_EMAIL_OR_PHONE'; payload: string }
  | { type: 'SET_IDENTIFIER_NONCE'; payload: string }
  | { type: 'SET_IDENTIFIER'; payload: string }
  | { type: 'SET_IS_LOADING'; payload: boolean }
  | { type: 'SET_LAST_SESSION_COOKIE'; payload: string }
  | { type: 'SET_LOGIN_CHECK_TIMER'; payload: any }
  | { type: 'SET_PROJECT'; payload: Project | undefined }
  | { type: 'SET_REDIRECT_URL'; payload: string }
  | { type: 'SET_REMEMBER_ME'; payload: boolean }
  | { type: 'SET_RESEND_WAIT'; payload: number }
  | { type: 'SET_SERVER_ERROR'; payload: string }
  | { type: 'SET_VERIFICATION_CODE'; payload: string }
  | { type: 'SET_VERIFIED_NONCE_TOKEN'; payload: string }
  | { type: 'SET_VERIFIED_PHONE_TOKEN'; payload: string }

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_ACTIVE_VIEW':
      return { ...state, activeView: action.payload };
    case 'SET_CLIENT_ERRORS':
      return { ...state, clientErrors: action.payload };
    case 'SET_IS_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_SERVER_ERROR':
      return { ...state, serverError: action.payload };
    case 'SET_EMAIL_OR_PHONE':
      return { ...state, emailOrPhone: action.payload };
    case 'SET_IDENTIFIER':
      return { ...state, identifier: action.payload };
    case 'SET_LAST_SESSION_COOKIE':
      return { ...state, lastSessionCookie: action.payload };
    case 'SET_REMEMBER_ME':
      return { ...state, rememberMe: action.payload };
    case 'SET_PROJECT':
      return { ...state, project: action.payload };
    case 'SET_IDENTIFIER_NONCE':
      return { ...state, identifierNonce: action.payload };
    case 'SET_VERIFICATION_CODE':
      return { ...state, verificationCode: action.payload };
    case 'SET_VERIFIED_PHONE_TOKEN':
      return { ...state, verifiedPhoneToken: action.payload };
    case 'SET_VERIFIED_NONCE_TOKEN':
      return { ...state, verifiedNonceToken: action.payload };
    case 'SET_RESEND_WAIT':
      return { ...state, resendWait: action.payload };
    case 'SET_ACCOUNT_STATUS':
      return { ...state, accountStatus: action.payload };
    case 'SET_CURRENT_LANGUAGE':
      return { ...state, currentLanguage: action.payload };
    case 'SET_LOGIN_CHECK_TIMER':
      return { ...state, loginCheckTimer: action.payload };
    case 'SET_REDIRECT_URL':
      return { ...state, redirectUrl: action.payload };
    default:
      return state;
  }
};

export const defaultState: State = {
  accountStatus: undefined,
  activeView: undefined,
  brand: undefined,
  clientErrors: [],
  currentLanguage: 'en',
  emailOrPhone: '',
  identifier: '',
  identifierNonce: undefined,
  isLoading: false,
  lastSessionCookie: '',
  loginCheckTimer: null,
  project: undefined,
  redirectUrl: '',
  rememberMe: false,
  resendCodeReady: false,
  resendWait: 0,
  serverError: '',
  verificationCode: '',
  verifiedNonceToken: '',
  verifiedPhoneToken: '',
};


const useAuthenticationStore = () => {
  const state = useContext(AuthenticationContext);
  const dispatch = useContext(AuthenticationDispatchContext);
  const configuration = useContext(AuthenticationConfigurationContext);
  const translate = useContext(TranslateContext);
  const navigate = useNavigate();
  var visitId = uuidv4();

  /**
 * Asynchronously retrieves a user-friendly error message from an Error object.
 * Currently has a number of checks because the error message signatures are inconsistent.
 *
 * @param {Error} error - The error object from which to extract the message.
 * @returns {Promise<string>} A promise that resolves to a string containing the error message.
 */
  async function getErrorMessage(error: Error) {
    let msgKey = error.message
    if (error.message === "AccountLockedOutCannotReset") {
      msgKey = "participant-login-error-account-locked-out-no-reset";
    }
    else if (error.message.indexOf("LockedOut") >= 0) {
      msgKey = "participant-login-error-account-locked-out";
    }
    else if (error.message === "InvalidUserNamePassword") {
      msgKey = "participant-login-error-invalid-password";
    }

    // @ts-ignore-next-line - error.cause is not a standard property, but is used here
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause
    // Check if the error message is in kebab-case or PascalCase and try to translate it instead
    if (error.cause?.message !== msgKey && !isKebabCase(error.cause?.message) && !isPascalCase(error.cause?.message)) {
      // @ts-ignore-next-line
      return error.cause?.message
    }

    try {
      const message = await translate(msgKey)
      return message
    } catch (error) {
      return msgKey;
    }
  }

  async function handlePhoneVerification(emailOrPhone: string) {
    dispatch({ type: 'SET_IS_LOADING', payload: true });
    try {
      const result = await sendVerificationCode(emailOrPhone)
      if (result.serverError) {
        dispatch({ type: 'SET_SERVER_ERROR', payload: result.serverError });
        return;
      }
      dispatch({ type: 'SET_RESEND_WAIT', payload: result.ResendWaitInSeconds });
      setActiveView(AuthenticationViews.verifyPhone)
    } catch (error: any) {
      const errorMessage = await getErrorMessage(error)
      dispatch({
        type: 'SET_SERVER_ERROR', payload: errorMessage
      })
    } finally {
      dispatch({ type: 'SET_IS_LOADING', payload: false });
    }
  }

  function clearErrors() {
    dispatch({
      type: 'SET_CLIENT_ERRORS', payload: []
    })
    dispatch({
      type: 'SET_SERVER_ERROR', payload: ''
    })
  }

  async function startLogin() {
    if (!isValidEmailOrPhone(state.emailOrPhone)) { return; }
    clearErrors()

    try {
      dispatch({ type: 'SET_IS_LOADING', payload: true });
      const newAccountStatus = await getAccountStatus(state.emailOrPhone)
      dispatch({
        type: 'SET_ACCOUNT_STATUS', payload: newAccountStatus
      });
      dispatch({ type: 'SET_IS_LOADING', payload: false });
      if (state.serverError) {
        dispatch({ type: 'SET_SERVER_ERROR', payload: state.serverError });
        return;
      }
      if (!newAccountStatus?.Exists) {
        dispatch({
          type: 'SET_CLIENT_ERRORS',
          payload: [...state.clientErrors, 'noAccountLogin']
        })
        trackRegistrationEvent("Account Does Not Exist Error");
      } else {
        dispatch({
          type: 'SET_EMAIL_OR_PHONE', payload: newAccountStatus.NormalizedEmailOrPhone
        });
        setActiveView(AuthenticationViews.loginPassword)
      }
    } catch (error: any) {
      const errorMessage = await getErrorMessage(error)
      dispatch({
        type: 'SET_SERVER_ERROR', payload: errorMessage
      })
    }
  }

  async function startJoin() {
    if (!isValidEmailOrPhone(state.emailOrPhone)) { return; }
    clearErrors()
    dispatch({ type: 'SET_IS_LOADING', payload: true });

    try {
      const accountStatus = await getAccountStatus(state.emailOrPhone)
      dispatch({ type: 'SET_IS_LOADING', payload: false });
      if (accountStatus?.Exists) {
        dispatch({
          type: 'SET_EMAIL_OR_PHONE', payload: accountStatus.NormalizedEmailOrPhone
        });
        setActiveView(AuthenticationViews.loginPassword)
      } else if (accountStatus?.ExistsInExternalProvider) {
        dispatch({
          type: 'SET_CLIENT_ERRORS', payload: [...state.clientErrors, 'externalAccountExists']
        })

        trackRegistrationEvent("External Account Already Exists Error");
        if (accountStatus.RedirectUrl) {
          window.setTimeout(function () { window.location.href = accountStatus.RedirectUrl; }, 3000);
        }
      } else {
        dispatch({
          type: 'SET_EMAIL_OR_PHONE', payload: accountStatus.NormalizedEmailOrPhone
        })
        if (accountStatus.Type == 'Email') {
          setActiveView(AuthenticationViews.setPassword)
        } else {
          await handlePhoneVerification(state.emailOrPhone);
        }
      }
    } catch (error: any) {
      const errorMessage = await translate(error.message)
      dispatch({
        type: 'SET_SERVER_ERROR', payload: errorMessage
      });
    } finally {
      dispatch({ type: 'SET_IS_LOADING', payload: false });
    }
  }

  var loadProject = async function (redirectUrl: string) {
    let newRedirectUrl = redirectUrl
    async function setLoadedProject(project: Project, showJoinIfNotEnrolled: boolean, isKnownUser: boolean) {
      if (project) {
        dispatch({
          type: 'SET_PROJECT', payload: project
        });
        const rootElement = document.querySelector(':root') as HTMLElement;
        if (rootElement?.style) {
          rootElement.style.setProperty('--project-color', project.OrganizationHexColor);
        }

        if (isKnownUser) {
          setActiveView(AuthenticationViews.knownUserLogin)
        }
        else if (project.EmailOrPhone) {
          dispatch({
            type: 'SET_EMAIL_OR_PHONE', payload: project.EmailOrPhone
          });
          setActiveView(AuthenticationViews.registrationCheck)
        }
        else if (showJoinIfNotEnrolled && !project.Enrolled) {
          dispatch({
            type: 'SET_EMAIL_OR_PHONE', payload: ""
          });
          setActiveView(AuthenticationViews.join)
        } else if (project.Enrolled) {
          setActiveView(AuthenticationViews.login)
        }
      }
    }
    // If there's a known user, we dont want to redirect on loading a project
    const knownUserEmailOrPhone = getLoginCookie()

    const projectCode = getProjectCode();
    if (projectCode) {
      try {
        const fetchedProject = await getProjectByCode(projectCode);
        await setLoadedProject(fetchedProject, false, !!knownUserEmailOrPhone);
      } catch (error: any) {
        const errorMessage = await getErrorMessage(error)
        dispatch({
          type: 'SET_SERVER_ERROR', payload: errorMessage
        })
      }
    }

    var linkKey = getLinkKey();
    if (linkKey) {
      try {
        const fetchedProject = await getProjectByLinkKey(linkKey);
        await setLoadedProject(fetchedProject, true, !!knownUserEmailOrPhone);
      } catch (error: any) {
        const errorMessage = await getErrorMessage(error)
        dispatch({
          type: 'SET_SERVER_ERROR', payload: errorMessage
        })
      }
    }

    if (projectCode) {
      newRedirectUrl = updateSearchParams(newRedirectUrl, { projectCode });
    }
    if (linkKey) {
      newRedirectUrl = updateSearchParams(newRedirectUrl, { linkKey });
    }
    dispatch({
      type: 'SET_REDIRECT_URL', payload: newRedirectUrl
    });
    return newRedirectUrl;
  }

  async function getRegistrationStatus(emailOrPhone: string) {
    if (!isValidEmailOrPhone(emailOrPhone)) { return; }
    clearErrors()

    try {
      dispatch({ type: 'SET_IS_LOADING', payload: true });
      const accountStatus = await getAccountStatus(emailOrPhone)
      dispatch({
        type: 'SET_ACCOUNT_STATUS', payload: accountStatus
      });
      dispatch({
        type: 'SET_EMAIL_OR_PHONE', payload: accountStatus.NormalizedEmailOrPhone
      });
      if (accountStatus?.Exists) {
        setActiveView(AuthenticationViews.loginPassword)
      } else if (accountStatus.Type === 'Email') {
        setActiveView(AuthenticationViews.setPassword);
      } else {
        await handlePhoneVerification(accountStatus.NormalizedEmailOrPhone);
      }
    } catch (error: any) {
      const errorMessage = await getErrorMessage(error)
      dispatch({
        type: 'SET_SERVER_ERROR', payload: errorMessage
      })
    } finally {
      dispatch({ type: 'SET_IS_LOADING', payload: false });
    }
  }

  async function validateIdentifier(identifier: string, redirectUrl: string) {
    let newRedirectUrl = redirectUrl;
    if (identifier) {
      clearErrors()
      dispatch({
        type: 'SET_IDENTIFIER', payload: identifier
      });
      try {
        const result = await postValidateIdentifier(getProjectCode() || '', identifier)
        if (!result) {
          dispatch({
            type: 'SET_IDENTIFIER_NONCE', payload: ''
          });
          dispatch({
            type: 'SET_SERVER_ERROR', payload: "Invalid identifier.  Please enter your participant identifier in order to enroll."
          })
          return redirectUrl;
        }

        dispatch({
          type: 'SET_IDENTIFIER_NONCE', payload: result.IdentifierNonce
        });
        if (redirectUrl) {
          newRedirectUrl = updateSearchParams(redirectUrl, { identifierNonce: result.IdentifierNonce })
          dispatch({
            type: 'SET_REDIRECT_URL', payload: newRedirectUrl
          });
        }
        setActiveView(AuthenticationViews.join)
      } catch (error: any) {
        const errorMessage = await getErrorMessage(error)
        dispatch({
          type: 'SET_SERVER_ERROR', payload: errorMessage
        })
      }
    }

    return newRedirectUrl;
  }

  async function finishLogin(password: string) {
    if (!isValidPassword(password)) { return; }
    clearErrors()

    dispatch({ type: 'SET_IS_LOADING', payload: true });
    try {
      await postFinishLogin(state.emailOrPhone, password);
      dispatch({
        type: 'SET_PASSWORD', payload: ""
      });
      setLoginCookie(state.emailOrPhone, state.rememberMe);
      window.location.href = makeUrl(state.redirectUrl);
    } catch (error: any) {
      const errorMessage = await getErrorMessage(error)
      dispatch({
        type: 'SET_SERVER_ERROR', payload: errorMessage
      })
    }
    dispatch({ type: 'SET_IS_LOADING', payload: false });
  };

  async function attemptVerifyPhone(newVerificationCode: string) {
    clearErrors()
    dispatch({
      type: 'SET_VERIFICATION_CODE', payload: newVerificationCode
    });
    if (!isValidVerificationCode(newVerificationCode)) { return; }
    dispatch({ type: 'SET_IS_LOADING', payload: true });

    try {
      const newVerifiedPhoneToken = await verifyPhone(state.emailOrPhone, newVerificationCode)
      dispatch({ type: 'SET_IS_LOADING', payload: false });
      dispatch({
        type: 'SET_VERIFIED_PHONE_TOKEN', payload: newVerifiedPhoneToken
      });
      setActiveView(AuthenticationViews.setPassword)
    } catch (error: any) {
      const errorMessage = await getErrorMessage(error)
      dispatch({
        type: 'SET_SERVER_ERROR', payload: errorMessage
      })
      trackRegistrationEvent("Phone Verification Failed");
      dispatch({ type: 'SET_IS_LOADING', payload: false });
    }
  };

  async function finishJoin(password: string) {
    if (!isValidPassword(password)) { return; }
    clearErrors()

    dispatch({ type: 'SET_IS_LOADING', payload: true });
    var joinRequestComplete = async () => {
      dispatch({
        type: 'SET_PASSWORD', payload: ""
      });
      dispatch({ type: 'SET_IS_LOADING', payload: false });
      await trackRegistrationEvent("Join Complete")
      setLoginCookie(state.emailOrPhone, state.rememberMe);
      window.location.href = makeUrl(state.redirectUrl);
    };

    if (validateEmail(state.emailOrPhone)) {
      try {
        await finishEmailJoin(state.emailOrPhone, password, getUtmParameters(), getClickIdParameters())
        await joinRequestComplete()
      } catch (error: any) {
        const errorMessage = await getErrorMessage(error)
        dispatch({
          type: 'SET_SERVER_ERROR', payload: errorMessage
        })
        dispatch({ type: 'SET_IS_LOADING', payload: false });
        trackRegistrationEvent("Join Error");
        return;
      }
    } else {
      try {
        await finishPhoneJoin(state.verifiedPhoneToken || '', password, getUtmParameters(), getClickIdParameters())
        await joinRequestComplete()
      } catch (error: any) {
        const errorMessage = await getErrorMessage(error)
        dispatch({
          type: 'SET_SERVER_ERROR', payload: errorMessage
        })
        dispatch({ type: 'SET_IS_LOADING', payload: false });
        trackRegistrationEvent("Join Error");
        return;
      }
    }
  };

  function loginExternal() {
    dispatch({ type: 'SET_IS_LOADING', payload: true });
    if (configuration.lastLoginLink.Href) {
      window.location.href = configuration.lastLoginLink.Href;
    } else if (configuration.lastIdp) {
      window.location.href = makeUrl(`/?IdentityProvider=${encodeURIComponent(configuration.lastIdp)}`);
    } else {
      window.location.href = makeUrl('/');
    }
  };

  async function resendVerificationCode() {
    trackRegistrationEvent("Resend Verification Code");
    try {
      dispatch({ type: 'SET_IS_LOADING', payload: true });
      const result = await sendVerificationCode(state.emailOrPhone)
      dispatch({ type: 'SET_RESEND_WAIT', payload: result.ResendWaitInSeconds });
    } catch (error: any) {
      const errorMessage = await getErrorMessage(error)
      dispatch({
        type: 'SET_SERVER_ERROR', payload: errorMessage
      })
    } finally {
      dispatch({ type: 'SET_IS_LOADING', payload: false });
    }
  };

  async function resendPasswordResetCode() {
    trackRegistrationEvent("Resend Password Reset Code");
    try {
      dispatch({ type: 'SET_IS_LOADING', payload: true });
      const result = await sendVerificationCodeForPasswordReset(state.emailOrPhone)
      dispatch({ type: 'SET_RESEND_WAIT', payload: result.ResendWaitInSeconds });
    } catch (error: any) {
      const errorMessage = await getErrorMessage(error)
      dispatch({
        type: 'SET_SERVER_ERROR', payload: errorMessage
      })
    } finally {
      dispatch({ type: 'SET_IS_LOADING', payload: false });
    }
  };

  async function forgotPassword() {
    if (validateEmail(state.emailOrPhone)) {
      dispatch({ type: 'SET_IS_LOADING', payload: true });
      try {
        await postForgotPassword(state.emailOrPhone)
        dispatch({ type: 'SET_IS_LOADING', payload: false });
        setActiveView(AuthenticationViews.verifyNonce)
      } catch (error: any) {
        const errorMessage = await getErrorMessage(error)
        dispatch({
          type: 'SET_SERVER_ERROR', payload: errorMessage
        })
      }
    } else {
      dispatch({ type: 'SET_IS_LOADING', payload: true });
      try {
        const result = await sendVerificationCodeForPasswordReset(state.emailOrPhone)
        dispatch({ type: 'SET_RESEND_WAIT', payload: result.ResendWaitInSeconds });
        setActiveView(AuthenticationViews.forgotPassword)
      } catch (error: any) {
        const errorMessage = await getErrorMessage(error)
        dispatch({
          type: 'SET_SERVER_ERROR', payload: errorMessage
        })
      }
    }
    dispatch({ type: 'SET_IS_LOADING', payload: false });
  };

  async function verifyPasswordResetNonce(emailOrPhone: string, nonce: string) {
    if (!isValidNonce(nonce)) { return; }
    clearErrors()

    dispatch({ type: 'SET_IS_LOADING', payload: true });
    try {
      const email = isValidEmail(emailOrPhone) ? emailOrPhone : null;
      const phone = email ? null : emailOrPhone;
      const verifiedNonceToken = await postVerifyPasswordResetNonce(email, phone, nonce)
      dispatch({
        type: 'SET_VERIFIED_NONCE_TOKEN', payload: verifiedNonceToken
      });
      setActiveView(AuthenticationViews.resetPassword)
    } catch (error: any) {
      const errorMessage = await getErrorMessage(error)
      dispatch({
        type: 'SET_SERVER_ERROR', payload: errorMessage
      })
      trackRegistrationEvent("Verify Password Reset Nonce Failure");
    }
    dispatch({ type: 'SET_IS_LOADING', payload: false });
  };

  async function verifyEmail(emailOrPhone: string, nonce: string, nativeAppRedirectUrl: string) {
    if (!isValidNonce(nonce)) { return; }
    clearErrors()
    dispatch({
      type: 'SET_IS_LOADING', payload: true
    });
    try {
      await postVerifyEmail(emailOrPhone, nonce)
      dispatch({ type: 'SET_IS_LOADING', payload: false });
      if (nativeAppRedirectUrl) {
        window.location.href = nativeAppRedirectUrl;
      }
    } catch (error: any) {
      const errorMessage = await getErrorMessage(error)
      dispatch({
        type: 'SET_SERVER_ERROR', payload: errorMessage
      })
    }
  };

  async function verifyEmailUpdate(clientStateID: string) {
    clearErrors()
    dispatch({
      type: 'SET_IS_LOADING', payload: true
    });
    try {
      await postVerifyEmailUpdate(clientStateID)
      dispatch({ type: 'SET_IS_LOADING', payload: false });
    } catch (error: any) {
      const errorMessage = await getErrorMessage(error)
      dispatch({
        type: 'SET_SERVER_ERROR', payload: errorMessage
      })
    }
  };

  async function verifyPhoneForPasswordReset(newVerificationCode: string) {
    dispatch({
      type: 'SET_VERIFICATION_CODE', payload: newVerificationCode
    });
    if (!isValidVerificationCode(newVerificationCode)) { return; }
    clearErrors()
    dispatch({
      type: 'SET_IS_LOADING', payload: true
    });
    try {
      const verifiedPhoneToken = await verifyPhone(state.emailOrPhone, newVerificationCode)
      dispatch({ type: 'SET_IS_LOADING', payload: false });
      dispatch({
        type: 'SET_VERIFIED_PHONE_TOKEN', payload: verifiedPhoneToken
      });
      setActiveView(AuthenticationViews.resetPassword)
    } catch (error: any) {
      const errorMessage = await getErrorMessage(error)
      dispatch({ type: 'SET_IS_LOADING', payload: false });
      dispatch({
        type: 'SET_SERVER_ERROR', payload: errorMessage
      })
    }
  };

  async function finishPasswordReset(password: string) {
    if (!isValidPassword(password)) { return; }
    clearErrors()
    dispatch({
      type: 'SET_IS_LOADING', payload: true
    });
    if (state.verifiedNonceToken) {
      try {
        await finishPasswordResetNonce(state.verifiedNonceToken, password);
        dispatch({
          type: 'SET_PASSWORD', payload: ""
        });
        setLoginCookie(state.emailOrPhone);
        window.location.href = makeUrl(state.redirectUrl);
      } catch (error: any) {
        const errorMessage = await getErrorMessage(error)
        dispatch({
          type: 'SET_SERVER_ERROR', payload: errorMessage
        })
        trackRegistrationEvent("Password Reset Failure");
      }
    } else {
      try {
        await finishPasswordResetPhone(state.verifiedPhoneToken, password);
        dispatch({
          type: 'SET_PASSWORD', payload: ""
        });
        setLoginCookie(state.emailOrPhone);
        window.location.href = makeUrl(state.redirectUrl);
      } catch (error: any) {
        const errorMessage = await getErrorMessage(error)
        dispatch({
          type: 'SET_SERVER_ERROR', payload: errorMessage
        })
        trackRegistrationEvent("Password Reset Failure");
      }
    }
    dispatch({
      type: 'SET_IS_LOADING', payload: false
    });
  }

  function isValidPassword(password: string) {
    if (!password) { return false; }
    if (password.length >= 8 && password.length <= 64) { return true; }
    return false;
  };

  function isCookiesBannerVisible() {
    return window.localStorage.getItem("hideCookiesBanner") !== "true";
  }

  function hideCookiesBanner() {
    window.localStorage.setItem("hideCookiesBanner", "true");
  }

  function locales() {
    if (state.project) {
      return configuration.availableLocales.filter((a: Locale) => state.project?.SupportedLocales.includes(a.Code));
    }
    return configuration.availableLocales || [];
  }

  function getLoginCookie() {
    const cookieValue = configuration.cookies.get('participant-login');
    if (cookieValue && isValidEmailOrPhone(cookieValue)) {
      return cookieValue;
    }
    return null;
  }

  function setLoginCookie(emailOrPhoneToSet: string, shouldrememberMe?: boolean) {
    var dt = new Date();
    if (shouldrememberMe) {
      configuration.cookies.put('participant-login', emailOrPhoneToSet, { expires: new Date(dt.getFullYear() + 1, dt.getMonth(), dt.getDate()) });
    } else {
      configuration.cookies.put('participant-login', null, { expires: new Date(dt.getFullYear() - 1, dt.getMonth(), dt.getDate()) });
    }
  }

  function setRememberMe(rememberMe: boolean) {
    dispatch({
      type: 'SET_REMEMBER_ME', payload: rememberMe
    })
    if (!rememberMe) {
      configuration.cookies.put('participant-login', "", { expires: new Date(0) });
    }
  }

  function checkLogin() {
    var sessionCookie = configuration.cookies.get(configuration.sessionCookieName);
    if (!state.lastSessionCookie && sessionCookie) {
      clearInterval(state.loginCheckTimer);
      window.location.href = makeUrl(state.redirectUrl);
    }
  }

  async function changeLanguage(lang: string) {
    try {
      dispatch({ type: 'SET_CURRENT_LANGUAGE', payload: lang });
      await translate.use(lang)
    } catch (error) {
      console.error("Error changing language", error);
    }
  };

  async function trackRegistrationEvent(eventType: string) {
    try {
      return postTrackRegistrationEvent(eventType, getPageName(), state.emailOrPhone, visitId, getUtmParameters(), getClickIdParameters(), state.project?.StudyCode);
    } catch {
      console.error("Failed to track event", eventType);
    }
  }

  function getPageName() {
    if (!state.activeView) return "";

    return state.activeView.split("-").map(function (w) { return capitalize(w); }).join(" ");
  }

  function setActiveView(view: AuthenticationViews, options?: NavigateOptions) {
    navigate("../" + view, options);
  }


  async function validatePasswordRequirements(password: string) {
    clearErrors();

    dispatch({ type: "SET_IS_LOADING", payload: true });
    let response = "";
    try {
      const result = await postValidatePasswordRequirements(state.emailOrPhone, password);
      response = result.Error as string;
    } catch (error: any) {
      const errorMessage = await getErrorMessage(error);
      dispatch({
        type: "SET_SERVER_ERROR",
        payload: errorMessage,
      });
      response = errorMessage;
    } finally {
      dispatch({ type: "SET_IS_LOADING", payload: false });
    }
    return response;
  }
  
  return {
    attemptVerifyPhone,
    changeLanguage,
    checkLogin,
    finishJoin,
    finishLogin,
    finishPasswordReset,
    forgotPassword,
    getErrorMessage,
    getPageName,
    hideCookiesBanner,
    isCookiesBannerVisible,
    isValidPassword,
    locales,
    loadProject,
    loginExternal,
    resendPasswordResetCode,
    resendVerificationCode,
    getLoginCookie,
    setLoginCookie,
    setActiveView,
    setRememberMe,
    startJoin,
    startLogin,
    trackRegistrationEvent,
    validateIdentifier,
    verifyEmail,
    verifyEmailUpdate,
    verifyPasswordResetNonce,
    verifyPhoneForPasswordReset,
    validatePasswordRequirements,
    getRegistrationStatus,
    state,
    dispatch,
  };
}

export default useAuthenticationStore;